/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useEffect, useState } from 'react';
import {
	Button,
	Dialog,
	DialogTitle,
	DialogActions,
	DialogContent,
	Typography,
	CircularProgress,
	Box,
	Table,
	TableContainer,
	Paper,
	Stack,
	IconButton,
	Tooltip,
	TableCell,
	TableRow,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { closeDialog } from '@/features/dialog/dialogSlice';
import { dealSearch } from '@/utils/typesense/clients/dealSearch';
import { Configure, InstantSearch } from 'react-instantsearch';
import { HitsTableBody } from '@/components/table/HitsTableBody';
import { FolioTableHead } from '@/components/table/FolioTableHead';
import { DealPolicyRow } from '@/components/tables/DealPolicyRow';
import { formatDateString } from '@/utils/constants';
import { showToast } from '@/features/toast/toastSlice';
import { useUpdateDealPolicyTransactionMutation } from '@/features/deals/dealsApi';
import { getDealClientId } from '@/features/deals/helper';
import { getClientIdFilters } from '@/features/clients/helper';
import { getYearFolderFormat } from '@/utils/date';

const policyHeadCells = [
	{
		id: '',
		numeric: false,
		label: '',
		align: 'left',
		sortable: false,
	},
	{
		id: 'year',
		numeric: false,
		label: 'Year',
		align: 'left',
		sortable: false,
	},
	{
		id: 'dealName',
		numeric: false,
		label: 'Deal Name',
		align: 'left',
		sortable: true,
	},
	{
		id: 'policies',
		numeric: false,
		label: 'Policies',
		align: 'left',
		sortable: false,
	},
	{
		id: 'dealStage',
		numeric: false,
		label: 'State/stage',
		align: 'left',
		sortable: false,
	},
];

const order = 'asc';
const orderBy = 'dealName';

export const MovePolicyTransactionsDialog = () => {
	const [loading, setLoading] = useState(false);
	const dispatch = useDispatch();

	const dialog = useSelector((state) => state.dialog);
	const open = dialog?.open?.movePolicyTransaction ?? false;
	const dialogData = dialog?.data?.movePolicyTransaction ?? {};

	const { client, deal, policy } = dialogData;

	const policyName = `INV:${policy?.InvoiceNumber} - ${policy?.BriefDescription} - ${formatDateString(policy?.FromDate)} - ${formatDateString(policy?.ToDate)}`;

	const clientFilter = getClientIdFilters(client);

	const [updateDealPolicyTransaction] = useUpdateDealPolicyTransactionMutation();

	const handleClose = () => {
		dispatch(closeDialog('movePolicyTransaction'));
	};

	const [selectedDeal, setSelectedDeals] = useState([]);

	const handleCheckboxChange = (event, dealId) => {
		if (event.target.checked) {
			setSelectedDeals((prev) => [dealId]);
		} else {
			setSelectedDeals((prev) => prev.filter((id) => id !== dealId));
		}
	};

	useEffect(() => {
		if (open) {
			setSelectedDeals([]);
			setLoading(false);
		}
	}, [open]);

	const movePolicyToDeal = async () => {
		setLoading(true);
		try {
			if (selectedDeal?.length > 0) {
				await updateDealPolicyTransaction({
					currentDealId: deal?.dealId,
					clientId: getDealClientId(deal),
					policyTransactionDocId: policy?.policyRow?.id,
					dealId: selectedDeal[0],
				})
					.unwrap()
					.then((response) => {
						dispatch(
							showToast({
								message: `Transaction INV:${policyName} successfully moved!`,
								action: {
									label: 'Moved Transaction',
								},
								autohide: false,
								duration: 5000,
							}),
						);
						handleClose();
					})
					.catch((error) => {
						dispatch(
							showToast({
								message: `Error moving transaction INV:${policyName}`,
								action: {
									label: 'Moving policy transaction',
								},
								autohide: false,
								type: 'error',
								duration: 5000,
							}),
						);
						handleClose();
						console.error('Error unlinking policy:', error);
					});
			}
		} catch (err) {
			console.error('Error adding policies', err);
			dispatch(showToast({ message: 'Error adding policies', error: true }));
		}
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth='lg'
			fullWidth
			sx={{ minWidth: '50%', padding: '1em' }}
		>
			<DialogTitle sx={{ fontWeight: 'bold' }} component='div'>
				Move Policy transaction
				<Typography variant='subtitle2'>
					{`Select the ${getYearFolderFormat(policy?.FromDate)} deal that relates to the transaction`}
					<Typography
						component='span'
						sx={{ ml: 0.5, fontWeight: 'bold', display: 'inline' }}
					>
						{policyName}
					</Typography>
				</Typography>
			</DialogTitle>
			<DialogContent sx={{ paddingBottom: '2em' }}>
				{!clientFilter ? (
					<Typography variant='subtitle1' color='error'>
						Unable to load deals: this transaction is missing a client reference.
					</Typography>
				) : (
					<InstantSearch indexName='deals' searchClient={dealSearch}>
						<Configure
							filters={`${clientFilter} && pipeline:!="${process.env.REACT_APP_PIPELINE_CLAIMS}" && yearFolder:="${getYearFolderFormat(policy?.FromDate)}"`}
						/>
						<TableContainer component={Paper} sx={{ marginTop: 2 }}>
							<Table>
								<FolioTableHead
									order={'desc'}
									orderBy={'createDate'}
									headCells={policyHeadCells}
									headerBackground={true}
								/>
								<HitsTableBody
									TableRowComponent={(props) => (
										<DealPolicyRow
											{...props}
											handleCheckboxChange={handleCheckboxChange}
											selectedDeal={selectedDeal}
										/>
									)}
									headCells={policyHeadCells}
									order={order}
									orderBy={orderBy}
									includePolicies={true}
									ErrorRowComponent={() => (
										<TableRow>
											<TableCell
												colSpan={policyHeadCells.length}
												align='center'
											>
												<Typography variant='subtitle1'>
													There was an error. Please try again.
												</Typography>
											</TableCell>
										</TableRow>
									)}
									EmptyRowComponent={() => (
										<TableRow>
											<TableCell
												colSpan={policyHeadCells.length}
												align='center'
											>
												<Typography variant='subtitle1'>
													{'No deals found'}
												</Typography>
											</TableCell>
										</TableRow>
									)}
								/>
							</Table>
						</TableContainer>
					</InstantSearch>
				)}
			</DialogContent>
			<DialogActions>
				<Button color='primary' onClick={handleClose} disabled={loading}>
					Cancel
				</Button>
				<Button
					onClick={movePolicyToDeal}
					variant='contained'
					color='primary'
					disabled={loading || selectedDeal.length === 0}
				>
					{loading ? (
						<Box sx={{ padding: '0 2em' }}>
							<CircularProgress size='1em' color='white' />
						</Box>
					) : (
						'Confirm'
					)}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
